import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ShieldCheck, Loader2, CheckCircle2, X, Clock, FileImage, ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  pending: { label: "En attente", className: "bg-amber-50 text-amber-700 border-amber-200" },
  verified: { label: "Vérifiée", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Refusee", className: "bg-red-50 text-red-700 border-red-200" },
};

function formatDate(d) { try { return format(new Date(d), "d MMM yyyy HH:mm", { locale: fr }); } catch { return d; } }

export default function AdminVerificationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Reject flow (reason only)
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (user.role !== "admin") { navigate("/"); return; }
    load(tab);
  }, [user, navigate, tab]);

  const load = async (status) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/verifications", { params: { status } });
      setUsers(data);
    } catch (err) {
      const code = err.response?.status;
      const detail = err.response?.data?.detail || err.message || "inconnu";
      toast.error(`Erreur chargement (HTTP ${code || "?"}) : ${detail}`);
    }
    finally { setLoading(false); }
  };

  const onApprove = async (u) => {
    if (!u) return;
    try {
      await api.post(`/admin/verifications/${u.id}/approve`);
      toast.success(`${u.email} vérifié`);
      load(tab);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const onReject = (u) => {
    setRejectReason("");
    setRejectTarget(u);
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    if (rejectReason.trim().length < 5) { toast.error("Motif requis (5 caractères min)"); return; }
    setRejecting(true);
    try {
      await api.post(`/admin/verifications/${rejectTarget.id}/reject`, {
        reason: rejectReason.trim(),
      });
      toast.success(`${rejectTarget.email} refusé`);
      setRejectTarget(null);
      setRejectReason("");
      load(tab);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setRejecting(false); }
  };

  if (!user || user.role !== "admin") return null;

  const photoUrl = (url) => url?.startsWith("/api/") ? `${BACKEND_URL}${url}` : url;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} className="rounded-xl gap-1" data-testid="back-to-admin">
        <ArrowLeft className="h-4 w-4" /> Retour au panneau admin
      </Button>

      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center"><ShieldCheck className="h-5 w-5 text-emerald-700" /></div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-zinc-950" data-testid="vérifications-title">Verifications d&apos;identité</h1>
          <p className="text-sm text-zinc-500">Verifiez les permis de conduire des propriétaires avant qu&apos;ils publient</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-zinc-100 rounded-xl p-1">
          <TabsTrigger value="pending" className="rounded-lg gap-2" data-testid="tab-pending"><Clock className="h-3.5 w-3.5" /> En attente</TabsTrigger>
          <TabsTrigger value="verified" className="rounded-lg gap-2" data-testid="tab-verified"><CheckCircle2 className="h-3.5 w-3.5" /> Verifies</TabsTrigger>
          <TabsTrigger value="rejected" className="rounded-lg gap-2" data-testid="tab-rejected"><X className="h-3.5 w-3.5" /> Refuses</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-emerald-700" /></div>
      ) : users.length === 0 ? (
        <Card className="border border-zinc-200 rounded-2xl">
          <CardContent className="py-12 text-center text-zinc-400 text-sm">Aucune demande dans cette catégorie</CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {users.map((u, i) => {
            const v = u.identity_verification || {};
            const meta = STATUS_BADGE[v.status] || STATUS_BADGE.pending;
            return (
              <Card key={u.id} className="border border-zinc-200 rounded-2xl overflow-hidden" data-testid={`vérification-card-${i}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <CardTitle className="font-heading text-base truncate">{u.name || "Sans nom"}</CardTitle>
                      <CardDescription className="text-xs truncate">{u.email}</CardDescription>
                    </div>
                    <Badge className={`${meta.className} border text-xs flex-shrink-0`}>{meta.label}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { url: v.photo_url, label: "Recto" },
                      { url: v.photo_url_2, label: "Verso" },
                      { url: v.selfie_url, label: "Selfie" },
                    ].map((p, idx) => (
                      p.url ? (
                        <button
                          key={p.label}
                          type="button"
                          onClick={() => setPreviewUrl(photoUrl(p.url))}
                          className="block w-full rounded-xl overflow-hidden border border-zinc-200 bg-zinc-50 hover:ring-2 hover:ring-emerald-300 transition"
                          data-testid={`open-photo-${i}-${idx + 1}`}
                          title={p.label}
                        >
                          <img src={photoUrl(p.url)} alt={p.label} className="w-full h-24 object-cover" />
                          <p className="text-[10px] text-center text-zinc-500 py-0.5">{p.label}</p>
                        </button>
                      ) : (
                        <div key={p.label} className="h-24 rounded-xl bg-zinc-50 border border-zinc-200 flex items-center justify-center text-zinc-300">
                          <FileImage className="h-6 w-6" />
                        </div>
                      )
                    ))}
                  </div>

                  <div className="text-xs text-zinc-500 space-y-0.5">
                    {v.submitted_at && <p>Soumis le {formatDate(v.submitted_at)}</p>}
                    {v.reviewed_at && <p>Examine le {formatDate(v.reviewed_at)}</p>}
                    {v.reject_reason && <p className="text-red-600 italic">&quot;{v.reject_reason}&quot;</p>}
                  </div>

                  {v.status === "pending" && (
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" className="rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 flex-1" onClick={() => onApprove(u)} data-testid={`approve-verif-${i}`}>
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approuver
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-xl text-red-600 border-red-200 hover:bg-red-50 flex-1" onClick={() => onReject(u)} data-testid={`reject-verif-${i}`}>
                        <X className="h-3.5 w-3.5 mr-1" /> Refuser
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Photo preview dialog */}
      <Dialog open={!!previewUrl} onOpenChange={(v) => !v && setPreviewUrl(null)}>
        <DialogContent className="rounded-2xl max-w-3xl p-2" data-testid="photo-preview-dialog">
          <DialogHeader className="sr-only">
            <DialogTitle>Aperçu du permis de conduire</DialogTitle>
          </DialogHeader>
          {previewUrl && <img src={previewUrl} alt="Permis (zoom)" className="w-full max-h-[80vh] object-contain rounded-xl" />}
        </DialogContent>
      </Dialog>

      {/* Reject reason dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(v) => { if (!v) { setRejectTarget(null); setRejectReason(""); } }}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="reject-reason-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">Refuser la vérification</DialogTitle>
            <DialogDescription>
              Indiquez le motif du refus (visible par {rejectTarget?.email}). Il pourra resoumettre de nouvelles photos.
            </DialogDescription>
          </DialogHeader>

          {/* Preset reasons (clickable chips) */}
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">Motifs frequents</p>
            <div className="flex flex-wrap gap-2">
              {[
                "Photo floue ou illisible",
                "Document non valide ou expiré",
                "Selfie ne correspond pas au document",
                "Document partiellement masque",
                "Informations personnelles non lisibles",
              ].map((preset, idx) => {
                const active = rejectReason.trim() === preset;
                return (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setRejectReason(preset)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition ${
                      active
                        ? "bg-red-600 text-white border-red-600"
                        : "bg-white text-zinc-700 border-zinc-200 hover:border-red-300 hover:bg-red-50"
                    }`}
                    data-testid={`reject-preset-${idx}`}
                  >
                    {preset}
                  </button>
                );
              })}
            </div>
          </div>

          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Sélectionnez un motif ci-dessus ou ecrivez le votre..."
            className="rounded-xl min-h-[80px]"
            maxLength={500}
            data-testid="reject-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setRejectTarget(null)}>Annuler</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700 rounded-xl" onClick={submitReject} disabled={rejecting || rejectReason.trim().length < 5} data-testid="reject-continue-btn">
              {rejecting ? "Envoi..." : "Refuser definitivement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
